import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CardPattern from '@mlb/react-patterns/components/Card';
import COLORS from '@mlb/react-patterns/style/universal.color';
import FixedAspect from '@mlb/react-patterns/components/utilComponents/FixedAspect';
import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton.tsx';
import { Elevation } from '@mlb/react-patterns/components/Card/card.types';
import { ReferenceWrapper } from '@mlb/react-patterns/components/Popover/popover.style.ts';

import { IMAGE_PLACEHOLDER_SVG } from '../../utils/constants';

import ReelCheck from '../ReelCheck';
import PlayIconComponent from '../PlayIcon';

export const Card = styled(CardPattern)`
    display: flex;
    flex-direction: column;
    font-family: ${({ theme }) => theme.fonts.family.primary};
    text-decoration: none; // If rendered as an anchor tag, remove underline

    ${({ disabled }) => disabled && `
        pointer-events: none;
    `}
`;

export const ImageWrapper = styled.div`
    position: relative;
    height: 100%;
    width: 100%;

    ${({ isActive }) => {

        if(isActive) {

            return `
                ::after {
                    align-items: center;
                    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5));
                    border: solid 2px ${COLORS.linkBlue};
                    border-radius: 8px 8px 0px 0px;
                    box-sizing: border-box;
                    color: #fff;
                    content: "NOW PLAYING";
                    display: flex;
                    font-size: 14px;
                    font-weight: bold;
                    height: 100%;
                    justify-content: center;
                    left: 0;
                    position: absolute;
                    top: 0;
                    width: 100%;
                }
            `;

        }
        return '';

    }};
;`;

export const PlayIcon = styled(PlayIconComponent)`
    position: absolute;
    left: ${({ theme }) => theme.card.overlayPaddingLeftRight};
    bottom: ${({ theme }) => theme.card.overlayPaddingTopBottom};
`;

export const Duration = styled.p`
    background: ${COLORS.mlbWhite};
    border-radius: 21px;
    box-sizing: border-box;
    color: ${COLORS.mlbBlack};
    font-size: 15px;
    font-weight: 700;
    margin: 0;
    min-width: 47px;
    padding: 2px 8px;
    position: absolute;
    right: ${({ theme }) => theme.card.overlayPaddingLeftRight};
    bottom: ${({ theme }) => theme.card.overlayPaddingTopBottom};
    text-align: center;
`;

const Image = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex: 1;
`;

const TextWrapper = styled.div`
    width: 100%;
    color: ${COLORS.mlbWhite};
    padding: 16px;
`;

const Title = styled.h3`
    height: 44px;
    font-weight: 600;
    font-size: 17px;
    line-height: 21px;
    margin: -3px 0;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
`;

const Date = styled.p`
    margin: 6px 0px 0px;
    font-size: 12px;
    line-height: 12px;
    color: ${COLORS.mlbGrey3};
`;

const OptionsWrapper = styled.div`
    display: flex;
    align-items: center;
    height: auto;

    ${ReferenceWrapper} {
        height: 100%;
    }
`;

const StatLine = styled.div`
    margin: 8px 0 0;
    display: flex;
    justify-content: space-between;
    font-size: 17px;
    line-height: 22px;
`;

export const Stat = ({ label, value }) => (
    <StatLine>
        {label}: <span>{value}</span>
    </StatLine>
);

const ContentCard = forwardRef((props, ref) => {

    const {
        as,
        date,
        duration,
        imageCuts,
        imageAltText,
        imageSizes,
        errorImageSrc,
        isActive,
        isInReel,
        loading,
        title,
        optionsComponent,
        statInfo,
        ...otherProps
    } = props;

    const src = imageCuts[0]?.src ?? IMAGE_PLACEHOLDER_SVG;
    const srcSet = imageCuts.map((img) => `${img.src} ${img.width}w`).join(', ');

    return (
        <Card forwardedAs={as} elevation={Elevation.NONE} {...otherProps} ref={ref}>
            <FixedAspect ratio={'16:9'}>
                {loading ? (
                    <Skeleton margin={0} />
                ) : (
                    <ImageWrapper isActive={isActive}>
                        <Image
                            alt={imageAltText}
                            src={src}
                            srcSet={srcSet}
                            sizes={imageSizes}
                            errorImageSrc={errorImageSrc}
                            loading="lazy"
                        />
                        {!isActive && <PlayIcon width={40} />}
                        {!isActive && <Duration>{duration}</Duration>}
                        {isInReel && <ReelCheck/>}
                    </ImageWrapper>
                )}
            </FixedAspect>
            <ContentWrapper>
                <TextWrapper>
                    {loading ? (
                        <>
                            <Skeleton margin={4} height={'17px'} />
                            <Skeleton margin={6} height={'17px'} />
                            <Skeleton margin={0} width={'30%'} height={'12px'} />
                        </>
                    ) : (
                        <>
                            <Title>{title}</Title>
                            <Date>{date}</Date>
                        </>
                    )}
                    {(!loading && statInfo) && <Stat {...statInfo} />}
                </TextWrapper>
                {optionsComponent && (
                    <OptionsWrapper>{optionsComponent}</OptionsWrapper>
                )}
            </ContentWrapper>

        </Card>
    );

});

ContentCard.propTypes = {
    date: PropTypes.string,
    duration: PropTypes.string,
    imageAltText: PropTypes.string,
    imageCuts: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
        })
    ),
    errorImageSrc: PropTypes.string,
    imageSizes: PropTypes.string,
    isActive: PropTypes.bool,
    isInReel: PropTypes.bool,
    optionsComponent: PropTypes.node,
    statInfo: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    }),
    title: PropTypes.string,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    as: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.node,
    ]),
};

ContentCard.defaultProps = {
    loading: false,
    isActive: false,
    isInReel: false,
    disabled: false,
};


export default ContentCard;
